import {html, LitElement, nothing} from 'lit';
import {customElement, property, query, state} from 'lit/decorators';

import {col, grow, hide, margin, padding, row} from "../styles";
import {extractTextAndScripts} from "../utils";
import {notificationService} from "../services/notificationService";
import pdqDataBroker from "../services/PdqDataBroker";

import Debug from 'debug';
import '@carbon/web-components/es/components/copy-button/index.js';
import '@carbon/web-components/es/components/icon-button/index.js';
import '@carbon/web-components/es/components/textarea/index.js';
import '@carbon/web-components/es/components/tag/index.js';
import '@carbon/web-components/es/components/form-group/index.js';
// import '@carbon/web-components/es/components/stack/index.js'; // causes issues!
import '@carbon/web-components/es/components/select/index.js';
import '@carbon/web-components/es/components/select/select-item.js';
import '@carbon/web-components/es/components/text-input/index.js';

import Icon from '@carbon/web-components/es/icons/data-table/16.js';
import Save from '@carbon/web-components/es/icons/save/16.js';
import pdqHotkeyService from "../services/PdqHotkeyService";
import {pdqDiscoveryLayer} from "../flow/_pdq-mixin";
import {TAG_TYPE} from "@carbon/web-components/es/components/tag/defs";
import { combine, map, ReplaySubject, staticObservable } from '../observable/ReplaySubject';
import {View} from "@finos/perspective";
import {LiquidVariable} from "./liquid-variable";
import {Liquid, Template} from "liquidjs";
import {unsafeHTML} from "lit-html/directives/unsafe-html";

import './pdq-liquid-variable'
import './pdq-liquid-variable-stack'

const liquid = new Liquid();

const log = Debug('pdq:liquid:pdq-liquid');
@customElement('pdq-liquid')
@pdqDiscoveryLayer(log, Icon, 'pdq-liquid', 'rgba(236, 255, 83, 0.13)', TAG_TYPE.CYAN)
export class PdqLiquid extends LitElement {
    static styles = [hide, row, col, grow, padding, margin]
    static next_id = 0
    _id = PdqLiquid.next_id++

    private _variables = new ReplaySubject<LiquidVariable[]>(1);
    private _template= new ReplaySubject<string>(1);
    private _liquidEngine= new ReplaySubject<Template[]>(1);
    private _data= new ReplaySubject<any>(1);
    private _scripts = [] as string[]

    @property() error: string|null = null;
    @property() show: boolean = false;
    @property({type: Array, reflect:true})
    get variables() { return this._variables.currentValue()! }
    set variables(value: LiquidVariable[]) { this._variables.next(value) }
    @state()
    get template(){
        return this._template.currentValue()
    }
    @state() dataPreview = 'no data';
    @state() rendered = 'loading...';
    @query(`cds-textarea.template`) templateTextArea: HTMLTextAreaElement|undefined;

    constructor() {
        super();
        this._variables.next([]);
        this._variables.subscribe(async i => {


            combine(staticObservable('test1'),staticObservable('test2')).subscribe(i => log(`---------------------- ${ i[0]} ${i[1]}`);


            log(`_variables changed`, i)

            if (i.length == 0){
                this._data.next({});
                return
            }

            log(`updating observableVariables...`);
            const observableVariables = this.variables.map((v: LiquidVariable) => {
                if (v.value) {
                    log(` DEBUG acquired static v: ${v.name} -> ${v.value}`)
                    return staticObservable(v)
                }
                if (!v.view) {
                    throw new Error(`Variable ${v.name} has no view`)
                }

                log(`  DEBUG waiting v: ${v.name} -> ${v.view}`)
                pdqDataBroker.getViewOrTable(v.view).subscribe(i => log('DEBUG indpendent sub', i))
                return map(pdqDataBroker.getViewOrTable(v.view), (view: View) => {
                    log(` DEBUG acquired v: ${v.name} -> ${v.view} -> ${view}`)
                    return {
                        id: v.id,
                        name: v.name,
                        view
                    }
                })
            })
            log(observableVariables)

            log(`unsub..`);
            this.unsub();
            const combinedVars = combine(...observableVariables)
            this.unsub = combinedVars.subscribe(async(variables: {
                id: string,
                name: string,
                view?: View
                value?: any
            }[]) => {
                log('subscribing to variables data changed', variables);
                const model = {} as any;

                function convertRow(row: any) {
                    return row;
                }

                await Promise.all(variables.map(async i => {
                    if (i.view)
                        model[i.name] = convertRow(await i.view.to_json());
                    else if (i.value)
                        model[i.name] = i.value!;
                }));

                log('data module updating... ', model)
                this._data.next(model);
            })
            log(`_variables changed complete`, combinedVars.currentValue())
        });
        combine(this._liquidEngine, this._data).subscribe(i => this._renderLiquid(i as any))
        this._template.subscribe((template: string) => {
            if (!template) {
                log(`template is blank... ignoring render: ${template}`);
                return
            }
            this._liquidEngine.next(liquid.parse(template));
        });
    }
    render()    {
        log(`${this.id} ${this.show} - render`)
        const slot = html`<slot class="hide" @slotchange="${this._onSlotChange}"></slot>`
        const variablesSlot = html`<slot name="variables" class="hide" @slotchange="${this._onVariablesSlotChange}"></slot>`
        const show = (this.show || pdqHotkeyService.showPdqData.currentValue());

        // if(show) setTimeout(() => {
        //     if (this.templateTextArea) {
        //         this.templateTextArea.style.overflow = 'hidden';
        //         this.templateTextArea.style.height = 'auto';
        //         this.templateTextArea.style.height = `${this.templateTextArea.scrollHeight}px`;
        //     }
        // }, 100);

        return html`
            ${slot}
            ${variablesSlot}
            ${!!this.error ? html`<cds-inline-notification kind="error" title="Liquid Template" subtitle="${this.error} ${typeof this.error}"></cds-inline-notification>` : nothing}
            ${show ? html`
                <div class="col show ${this.error ? 'error' : ''}">
                    ${this.renderToolBar(html`<cds-icon-button slot="tool-bar" @click="${this.save}" kind="primary">
                        <span slot="tooltip-content">Save</span>
                        ${Save({ slot: 'icon' })}
                        </cds-icon-button>
                    `)}
                    <div class="m-2">
                        <pdq-liquid-variable-stack
                            @pdq-liquid-variables-saved="${this.variablesChanged}"
                            .variables="${this.variables}"
                        >
                        </pdq-liquid-variable-stack>
                        <cds-textarea class="template" value="${this.template?.trim()}"><label slot="label-text">Liquid Template:</label></cds-textarea>
                    </div>
                </div>
                <hr>
                ` : nothing
           }${unsafeHTML(this.rendered)}`;
    }

    scriptAbortController = new AbortController();
    signal = this.scriptAbortController.signal;
    updated = () => {
        this.scriptAbortController.abort();
        this.scriptAbortController = new AbortController();
        this.signal = this.scriptAbortController.signal;
        for (const s of this._scripts) {
            eval(s);
        }
    }
    unsub = () => {}
    async variablesChanged(evt: CustomEvent){
        log('variablesChanged', evt.detail)
        this._variables.next(evt.detail);
    }
    async save(){
        log('save')
        if (!this.templateTextArea)
            return
        try {

            this._template.next(this.templateTextArea.value);
            this.requestUpdate('template')
            this.requestUpdate('variables')
        }catch(e: any){
            log(`save error: ${e}`)
            notificationService.error(`pdq-json: ${this.id} - Failed to save... Error parsing JSON: ${e?.message || e}`);
        }
    }

    async _renderLiquid([template, variables]: [Template[], any]){
        log('rendering liquid', template, variables);
        this.rendered = await liquid.render(template, variables);


        const varMap = {} as any
        this._variables.currentValue()?.map(i => varMap[i.name] = i.view)
        const vars = `<template slot="variables">${JSON.stringify(varMap, null, 2)}</template>`;
        this.dataPreview = `<pdq-liquid${this.id ? html` id="${this.id}"` : ''}>${ this._variables.currentValue() ? vars : ''}
<template>${this._template.currentValue()?.trim()}</template><pdq-liquid>
`;
        this.requestUpdate('rendered')
    };
    _onSlotChange = async (event: Event)=> {
        log(`${this.id} - slot change`);
        const slot = event.target as HTMLSlotElement;
        const nodes = slot.assignedNodes({ flatten: true });
        const {template, scripts} = extractTextAndScripts(nodes);
        log(`template: ${template}`)
        this._scripts = scripts;
        this._template.next(template);
        this.requestUpdate('template')
    }
    _onVariablesSlotChange = async (event: Event)=> {
        log(`${this.id} - variables slot change`);
        const slot = event.target as HTMLSlotElement;
        const nodes = slot.assignedNodes({ flatten: true });
        const {template, scripts} = extractTextAndScripts(nodes);
        try {
            const vars = JSON.parse(template);
            const newVariables = []
            let idx = 0;
            for (const key in vars) {
                newVariables.push({
                    id: idx++,
                    name: key,
                    view: vars[key]
                } as LiquidVariable)
            }
            log('variables switching to', newVariables)
            this._variables.next(newVariables);
        }
        catch(e: any)
        {
            log(`Error parsing JSON: \n${e?.message || e}`);
            notificationService.error(`pdq-liquid#${this.id}|Error parsing JSON: \n${e?.message || e}`);
        }
    }
}


// class DplLiquidComponent extends LitElement {
//     static properties = {
//         templateContent: { state: true },
//         isLoading: { state: true }
//     };
//
//     _data = {};
//     _model = {};
//     _parsedTemplate = null;
//     set data(value) {
//         console.log(`data[${typeof value}]:`, value);
//         if (value === this._data)
//             return;
//         try {
//             this._data = JSON.parse(value.replaceAll("'", '"'));
//             console.log('data is json. value=', this._data)
//         }catch (error) {
//             this._data = value;
//             console.log('data is not json. value=', this._data)
//         }
//         if(this._setupComplete)
//             this.updateTemplate();
//     }
//     get data() {
//         return this._data;
//     }
//
//     constructor() {
//         super();
//         this.queries = {};
//         this.templateContent = '';
//         this.isLoading = true;
//     }
//
//     async firstUpdated() {
//         const slot = this.shadowRoot.querySelector('slot');
//         slot.assignedNodes({flatten: true});
//
//         const nodes = slot.assignedNodes({flatten: true});
//
//         await this.updateTemplate();
//         this._setupComplete = true;
//     }
//
//     _scripts = [];
//     _lastTemplate = '---';
//     async updateTemplate() {
//         this.isLoading = true;
//
//         const {template, scripts} = this.getSlotContent();
//         this._scripts = scripts;
//         if (template !== this._lastTemplate) {
//             this._lastTemplate = template;
//             if (!this._engine)
//                 this._engine = new Liquid();
//             try {
//                 //this.templateContent = await this._engine.parse(template, {data: this._data, ...await this.queryResults()});
//                 this._parsedTemplate = this._engine.parse(template);
//             } catch (error) {
//                 console.error('Error rendering template:', error);
//             }
//         }
//
//         this._model = {data: this._data, ...await this.queryResults()};
//         this.templateContent = await this._engine.render(
//             this._parsedTemplate,
//             this._model
//         );
//         this.isLoading = false;
//     }
//
//     getSlotContent() {
//         const slot = this.shadowRoot.querySelector('slot');
//         const nodes = slot.assignedNodes({flatten: true});
//         let template = '';
//         let scripts = []
//         return extractHtml(nodes, template, scripts);
//     }
//
//     static styles = css`
//         :host {
//             display: block;
//             transition: opacity 0.3s ease;
//             opacity: 1;
//         }
//         :host([isLoading]) {
//             opacity: 0.5;
//         }
//         slot {
//             display: none;
//         }
//     `;
//
//     render() {
//         return html`
//                     <slot @slotchange="${this.updateTemplate}"></slot>
//                     ${unsafeHTML(this.templateContent)}
//                 `;
//     }
//
//     updated(changedProperties) {
//         super.updated(changedProperties);
//         if (this._scripts.length > 0)
//             setTimeout(this.runScripts, 300)
//     }
//     runScripts = () => {
//         const dataJson = JSON.stringify(this._model);
//         for (const s of this._scripts) {
//             eval(`const data = ${dataJson};
// ${s}`);
//         }
//     }
// }
